import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/shared/ui/feedback/toast';

import {
  applyDisplayRefs,
  decodeDisplayPayload,
  decodeViewPayload,
  encodeDisplayPayload,
  encodeViewPayload,
} from '../persistence/payload';
import type { ListPageServerState } from '../persistence/useListPageServerState';
import type { ListFilterFieldDef, ResolvedListPageConfig } from './types';
import type { ListPageController } from './useListPageController';
import { ALL_VIEW_ID, buildViews, viewSignature, type ListViewDef } from './viewsModel';

export interface ListPageViewsApi {
  views: ListViewDef[];
  activeViewId: string;
  /** Aktuální filtry se liší od definice aktivního pohledu (dirty tečka). */
  dirty: boolean;
  pick: (viewId: string) => void;
  /** Uloží aktuální filtry jako nový pohled a aktivuje ho. Vrací úspěch (dialog se zavírá jen při něm). */
  saveNew: (name: string, shared: boolean) => Promise<boolean>;
  /** „Uložit změny" — přepíše definici aktivního (vlastního) pohledu aktuálními filtry. */
  saveChanges: () => Promise<boolean>;
  /** „Přejmenovat & viditelnost" — definici filtrů nemění (posílá uložený payload beze změny). */
  rename: (viewId: string, name: string, shared: boolean) => Promise<boolean>;
  duplicate: (viewId: string) => Promise<boolean>;
  /** Smazání pohledu; aktivní pohled přepne na „Vše" (a tím spustí dotaz). */
  remove: (viewId: string) => Promise<boolean>;
  reorder: (fromId: string, toId: string) => void;
}

/**
 * Orchestrace pohledů (anatomie 01 §2) nad controllerem a centrální persistencí:
 * skládá systémové „Vše" + šablonové + serverové pohledy dle uživatelského pořadí,
 * řeší dirty detekci a CRUD se server zápisem (chyby toastem, definice jen autor).
 */
export function useListPageViews<TItem>(options: {
  controller: ListPageController<TItem>;
  config: ResolvedListPageConfig<TItem>;
  server: ListPageServerState;
  defs: Map<string, ListFilterFieldDef>;
}): ListPageViewsApi {
  const { controller, config, server, defs } = options;
  const { t } = useTranslation('listPage');

  const serverViews = useMemo(
    () =>
      server.views.map((view) => ({
        id: view.id,
        name: view.name,
        shared: view.shared,
        own: view.own,
        filters: decodeViewPayload(view.payload, defs),
        displayRefs: decodeDisplayPayload(view.displayPayload),
      })),
    [server.views, defs]
  );

  const views = useMemo(
    () =>
      buildViews({
        allLabel: t('views.all'),
        templateViews: config.templateViews,
        serverViews,
        viewOrder: controller.viewOrder,
      }),
    [t, config.templateViews, serverViews, controller.viewOrder]
  );

  const activeView = useMemo(
    () => views.find((view) => view.id === controller.activeViewId) ?? views[0],
    [views, controller.activeViewId]
  );

  const dirty = useMemo(() => {
    if (activeView.system) {
      return false;
    }
    return viewSignature(controller.filters, defs) !== viewSignature(activeView.filters, defs);
  }, [activeView, controller.filters, defs]);

  const fail = useCallback(
    (error: unknown): false => {
      void error;
      toast({ severity: 'error', description: t('persistence.saveFailed') });
      return false;
    },
    [t]
  );

  const pick = useCallback(
    (viewId: string) => {
      const view = views.find((v) => v.id === viewId);
      if (!view) {
        return;
      }
      const filters = applyDisplayRefs(view.filters, view.displayRefs, defs);
      controller.applyView(view.id, filters);
    },
    [views, defs, controller]
  );

  return {
    views,
    activeViewId: activeView.id,
    dirty,
    pick,

    saveNew: useCallback(
      async (name, shared) => {
        try {
          const id = await server.createView({
            name,
            shared,
            payload: encodeViewPayload(controller.filters, defs),
            displayPayload: encodeDisplayPayload(controller.filters, defs),
          });
          controller.setActiveView(id);
          return true;
        } catch (error) {
          return fail(error);
        }
      },
      [server, controller, defs, fail]
    ),

    saveChanges: useCallback(async () => {
      if (activeView.system || activeView.template || !activeView.own) {
        return false;
      }
      try {
        await server.updateView(activeView.id, {
          name: activeView.label,
          shared: activeView.shared,
          payload: encodeViewPayload(controller.filters, defs),
          displayPayload: encodeDisplayPayload(controller.filters, defs),
        });
        return true;
      } catch (error) {
        return fail(error);
      }
    }, [activeView, server, controller.filters, defs, fail]),

    rename: useCallback(
      async (viewId, name, shared) => {
        const raw = server.views.find((v) => v.id === viewId);
        if (!raw) {
          return false;
        }
        try {
          await server.updateView(viewId, {
            name,
            shared,
            payload: raw.payload,
            displayPayload: raw.displayPayload,
          });
          return true;
        } catch (error) {
          return fail(error);
        }
      },
      [server, fail]
    ),

    duplicate: useCallback(
      async (viewId) => {
        const view = views.find((v) => v.id === viewId);
        if (!view || view.system) {
          return false;
        }
        const raw = server.views.find((v) => v.id === viewId);
        try {
          const id = await server.createView({
            name: t('views.copyName', { name: view.label }),
            shared: false,
            payload: raw?.payload ?? encodeViewPayload(view.filters, defs),
            displayPayload: raw?.displayPayload ?? null,
          });
          controller.setActiveView(id);
          return true;
        } catch (error) {
          return fail(error);
        }
      },
      [views, server, controller, defs, t, fail]
    ),

    remove: useCallback(
      async (viewId) => {
        try {
          await server.deleteView(viewId);
          if (controller.activeViewId === viewId) {
            controller.applyView(ALL_VIEW_ID, []);
          }
          return true;
        } catch (error) {
          return fail(error);
        }
      },
      [server, controller, fail]
    ),

    reorder: useCallback(
      (fromId, toId) => {
        if (fromId === toId || fromId === ALL_VIEW_ID || toId === ALL_VIEW_ID) {
          return;
        }
        const order = views.filter((v) => !v.system).map((v) => v.id);
        const from = order.indexOf(fromId);
        const to = order.indexOf(toId);
        if (from < 0 || to < 0) {
          return;
        }
        const next = [...order];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        controller.setViewOrder(next);
      },
      [views, controller]
    ),
  };
}
